<template>
  <!-- Loading -->
  <template v-if="loading">
    <v-row density="comfortable">
      <v-col v-for="index in 12" :key="index" cols="12" sm="6" md="4" :lg="cardsLg" :xl="cardsXl">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>
  </template>

  <list-content v-else-if="products.length > 0">
    <!-- Products -->
    <v-col
      v-for="(product, index) in products"
      :key="index"
      v-intersect.once="index === products.length - 2 ? loadNextPage : ''"
      :cols="cardsCols"
      :sm="cardsSm"
      :md="cardsMd"
      :lg="cardsLg"
      :xl="cardsXl"
    >
      <product-card
        show-product-identifiers
        :add-to-cart-event="addToCartEvent"
        :loading-realtime-price="loadingRealtimePrices.has(product.productId)"
        :product="product"
        :tabindex="index === 0 ? 0 : -1"
        :is-focused="currentFocusIndex === index"
        @keydown="onKeyDown"
        @focus="onItemFocus(index)"
        @blur="onItemBlur(index)"
      />
    </v-col>
    <v-col v-if="loadingNextPage" cols="12" class="d-flex justify-center">
      <v-progress-circular indeterminate :size="50" color="primary" class="ma-1" />
    </v-col>
  </list-content>

  <!-- Empty Message -->
  <list-empty
    v-else-if="products.length === 0"
    :icon="mdiStore"
    :message="keywordTooShortError ? $t('error.keywordTooShort') : $t('noProductsFound')"
  />
</template>

<script setup lang="ts">
import ListContent from '@/components/list/ListContent.vue'
import ListEmpty from '@/components/list/ListEmpty.vue'
import ProductCard from '@/modules/product/components/ProductCard.vue'
import useProductSearch from '@/modules/product/useProductSearch'
import useRealtimePrices from '@/modules/product/useRealtimePrices'
import AddToCartButtonEvents from '@/modules/tracking/events/helper/addToCartButton'
import { MediumProduct } from '@/types/index'
import { useArrowKeyGridNavigation } from '@/utils/useArrowKeyGridNavigation'
import { mdiStore } from '@mdi/js'
import { storeToRefs } from 'pinia'
import { computed, PropType } from 'vue'
import { useDisplay } from 'vuetify/lib/composables/display.mjs'

const props = defineProps({
  addToCartEvent: {
    type: Object as PropType<AddToCartButtonEvents>,
    required: true
  },
  products: {
    type: Array as PropType<MediumProduct[]>,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  loadingNextPage: {
    type: Boolean,
    default: false
  },
  cardsLg: {
    type: Number,
    default: 3
  },
  cardsXl: {
    type: Number,
    default: 2
  }
})

const emit = defineEmits<{
  (e: 'loadNextPage'): void
}>()

const { keywordTooShortError } = storeToRefs(useProductSearch())
const { coladaLoading: loadingRealtimePrices } = storeToRefs(useRealtimePrices())
const { smAndDown, md, lg, xlAndUp } = useDisplay()

const loadNextPage = (_entries: unknown, _observer: unknown, isIntersecting: boolean): void => {
  if (isIntersecting) {
    emit('loadNextPage')
  }
}

const cardsCols = 12
const cardsSm = 6
const cardsMd = 4

const cardsPerRow = computed(() => {
  if (smAndDown.value) return cardsCols / cardsSm
  if (md.value) return cardsCols / cardsMd
  if (lg.value) return cardsCols / props.cardsLg
  if (xlAndUp.value) return cardsCols / props.cardsXl
  return cardsCols
})

const numberOfProducts = computed(() => props.products.length)

const { currentFocusIndex, onItemFocus, onItemBlur, onKeyDown } = useArrowKeyGridNavigation(
  cardsPerRow,
  numberOfProducts
)
</script>
